import { z } from "zod";

/**
 * Shared DTO cho Admin Broadcast & In-app Banner (US-AE13.F1-0044 · @UC9120 broadcast-banner). MỘT
 * nguồn sự thật cho api producer (S02), admin web (S04).
 *
 * Broadcast = admin soạn nội dung song ngữ vi+en + chọn segment → enqueue OutboxEvent (producer-only,
 * reuse dispatcher AE7 — KHÔNG build kênh gửi). Dispatcher render theo locale người nhận + enforce
 * quiet-hours (task 0045) + respect preferences (trừ mandatory).
 *
 * BẤT BIẾN (invariants):
 * - Locale ĐỦ vi+en (C5): dispatcher render đúng ngôn ngữ người nhận — thiếu 1 → reject.
 * - no-SMS: segmentType ∈ {all,group,cohort} — KHÔNG kênh/segment lạ (sms…).
 * - group cần segmentRef.groupId; cohort chưa có nguồn → api feature-off 400 (KHÔNG bịa filter).
 * - scheduledAt (nếu có) ≥ now (C5 validate biên) — KHÔNG lịch quá khứ.
 * - `.strict()` chống over-post. KHÔNG field ghi/sửa tiền (G2). KHÔNG nhúng PII (segmentRef chỉ ref id).
 */

// ============================================================
// Enum-lite
// ============================================================

/** Đối tượng nhận broadcast. all = toàn hệ; group = 1 nhóm; cohort = tập lọc (v1 feature-off). no-SMS. */
export const broadcastSegmentTypeSchema = z.enum(["all", "group", "cohort"]);
export type BroadcastSegmentType = z.infer<typeof broadcastSegmentTypeSchema>;

/** Vòng đời broadcast. draft (nháp) · scheduled (đặt lịch) · enqueued (đã đẩy outbox) · sent · cancelled. */
export const broadcastStatusSchema = z.enum([
  "draft",
  "scheduled",
  "enqueued",
  "sent",
  "cancelled",
]);
export type BroadcastStatus = z.infer<typeof broadcastStatusSchema>;

// ============================================================
// Segment ref (con trỏ — KHÔNG PII)
// ============================================================

/** Ref segment: group → {groupId}; cohort → {filter} (v1 chưa dùng). KHÔNG nhúng phone/email. */
export const broadcastSegmentRefSchema = z
  .object({
    groupId: z.string().uuid().optional(),
    filter: z.record(z.unknown()).optional(),
  })
  .strict();
export type BroadcastSegmentRef = z.infer<typeof broadcastSegmentRefSchema>;

// ============================================================
// Create input (POST /admin/broadcasts)
// ============================================================

export const BROADCAST_TITLE_MAX = 200;
export const BROADCAST_BODY_MAX = 4000;

/**
 * Body tạo broadcast (AC1-AC4). Locale ĐỦ vi+en (title+body non-empty). segmentType enum; group cần
 * segmentRef.groupId (refine). scheduledAt optional ISO ≥ now (refine — gửi ngay nếu vắng). mandatory
 * default false. `.strict()` chống over-post.
 */
export const createBroadcastSchema = z
  .object({
    titleVi: z.string().trim().min(1).max(BROADCAST_TITLE_MAX),
    titleEn: z.string().trim().min(1).max(BROADCAST_TITLE_MAX),
    bodyVi: z.string().trim().min(1).max(BROADCAST_BODY_MAX),
    bodyEn: z.string().trim().min(1).max(BROADCAST_BODY_MAX),
    segmentType: broadcastSegmentTypeSchema,
    segmentRef: broadcastSegmentRefSchema.optional(),
    scheduledAt: z.string().datetime({ offset: true }).optional(),
    mandatory: z.boolean().optional().default(false),
    /**
     * asDraft=true → LƯU NHÁP (status=draft): KHÔNG gửi, KHÔNG enqueue outbox, KHÔNG cần scheduledAt.
     * Cho phép soạn dở rồi lưu (thay nút "Lưu nháp" chỉ-toast trước đây). Mặc định false.
     */
    asDraft: z.boolean().optional().default(false),
  })
  .strict()
  .refine((v) => v.segmentType !== "group" || v.segmentRef?.groupId != null, {
    message: "segmentType=group cần segmentRef.groupId",
    path: ["segmentRef", "groupId"],
  })
  .refine((v) => v.scheduledAt == null || new Date(v.scheduledAt).getTime() >= Date.now(), {
    message: "scheduledAt không được ở quá khứ",
    path: ["scheduledAt"],
  });

export type CreateBroadcastInput = z.infer<typeof createBroadcastSchema>;

// ============================================================
// List item + response (GET /admin/broadcasts)
// ============================================================

/** 1 dòng broadcast trên list. bodyVi/En KHÔNG trả ở list (chỉ title) — nhẹ payload; detail xem riêng. */
export const broadcastListItemSchema = z
  .object({
    id: z.string(),
    titleVi: z.string(),
    titleEn: z.string(),
    segmentType: broadcastSegmentTypeSchema,
    scheduledAt: z.string().nullable(),
    mandatory: z.boolean(),
    status: broadcastStatusSchema,
    createdBy: z.string(),
    createdAt: z.string(),
  })
  .strict();

export type BroadcastListItem = z.infer<typeof broadcastListItemSchema>;

/** Envelope list: `{ data, pageInfo }` (cursor DRY — nhất quán admin contracts hiện hữu). */
export const broadcastListResponseSchema = z
  .object({
    data: z.array(broadcastListItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type BroadcastListResponse = z.infer<typeof broadcastListResponseSchema>;

// ============================================================
// List query
// ============================================================

export const BROADCAST_LIST_MAX_LIMIT = 100;

/** Query list — filter status, cursor + limit. `.strict()` chặn param lạ. */
export const broadcastListQuerySchema = z
  .object({
    status: broadcastStatusSchema.optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(BROADCAST_LIST_MAX_LIMIT).optional(),
  })
  .strict();

export type BroadcastListQuery = z.infer<typeof broadcastListQuerySchema>;

// ============================================================
// Outbox event (producer contract — dispatcher AE7 tiêu thụ)
// ============================================================

/** eventType broadcast enqueue. Dispatcher AE7 expand segment + render locale + check preference/quiet-hours. */
export const BROADCAST_REQUESTED_EVENT = "broadcast.requested";
